/**
 * AbnRegistryDemoPage
 * ───────────────────
 * Live walkthrough of the central ABN registry — paste any ABN, see it
 * pre-fill from cache, see which modules already use it.
 *
 *   Demo at: /demo/abn-registry
 *
 * Useful pitch beats:
 *   • "One ABN, one record, every module reads the same data"
 *   • "Type once, register everywhere"
 *   • "Cached responses are instant, ABR refresh happens in background"
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Database, Sparkles, Clock, Building2, ArrowRight, RefreshCw } from "lucide-react";
import AbnLookup from "@/components/AbnLookup";

const MODULES = [
  { key: "directory", label: "Directory", desc: "Public business listings" },
  { key: "plugin",    label: "WP Plugin", desc: "HustleConnect site loader" },
  { key: "plate",     label: "Plate",     desc: "Restaurant + booking SaaS" },
  { key: "plaza",     label: "Plaza",     desc: "Co-op marketplace" },
  { key: "property",  label: "Property",  desc: "Real-estate platform" },
  { key: "deals",     label: "Deals",     desc: "Voucher / loyalty exchange" },
  { key: "chat",      label: "Chat",      desc: "HustleChat workspaces" },
  { key: "music",     label: "Music",     desc: "Local musicians hub" },
];

export default function AbnRegistryDemoPage() {
  const [abn, setAbn] = useState("");
  const [verified, setVerified] = useState(null);
  const [backfill, setBackfill] = useState(null);

  // Poll the directory-ABN backfill stats every 15 seconds so the demo
  // page reflects live progress without a hard refresh.
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
    let cancelled = false;
    const load = () => {
      fetch(`${API}/api/abn/backfill/status`)
        .then((r) => r.json())
        .then((d) => { if (!cancelled) setBackfill(d); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 to-amber-50">
      <header className="border-b border-stone-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-amber-700" />
            <h1 className="font-bold text-stone-900 tracking-tight">ABN Registry</h1>
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300">
              Live
            </span>
          </div>
          <Link to="/demo" className="text-[12px] font-semibold text-stone-700 hover:text-stone-900">
            ← back to demo playbook
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 grid lg:grid-cols-2 gap-8">
        {/* LEFT — interactive lookup */}
        <section className="space-y-5">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-bold mb-1">
              Step 1 · paste any ABN
            </p>
            <h2 className="text-2xl font-bold text-stone-900 mb-3 leading-tight">
              One record. Every module. Auto-populate everywhere.
            </h2>
            <p className="text-[13px] text-stone-600">
              The first time we see an ABN we hit the ATO&apos;s ABN Lookup directly. Every lookup
              after that comes straight from our registry — ~5 ms, no rate limits, no waiting.
              Whenever any module registers a business, the link is recorded here so the next
              module can pre-fill the form automatically.
            </p>
          </div>

          <AbnLookup
            value={abn}
            onChange={setAbn}
            onResolve={setVerified}
            testId="demo-abn"
            label="Try it — paste any ABN (spaces or dashes are fine)"
          />

          <div className="rounded-lg bg-white border border-stone-200 p-4 text-[12px] text-stone-600 space-y-2">
            <p className="font-bold text-stone-900 flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5 text-amber-600" /> Try these
            </p>
            <ul className="space-y-1 font-mono text-[12px]">
              <li>
                <button
                  onClick={() => setAbn("16 160 565 775")}
                  className="hover:underline text-amber-700"
                  data-testid="try-local-business"
                >
                  16 160 565 775
                </button> — Local Business Services (7 trading names)
              </li>
              <li>
                <button
                  onClick={() => setAbn("33051775556")}
                  className="hover:underline text-amber-700"
                  data-testid="try-telstra"
                >
                  33051775556
                </button> — Telstra
              </li>
              <li>
                <button
                  onClick={() => setAbn("48 123 123 124")}
                  className="hover:underline text-amber-700"
                  data-testid="try-cba"
                >
                  48 123 123 124
                </button> — Commonwealth Bank
              </li>
            </ul>
          </div>
        </section>

        {/* RIGHT — result + module grid */}
        <section className="space-y-5">
          <div className="rounded-xl bg-white border border-stone-200 p-5 shadow-sm">
            <p className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-2">
              Resolved business
            </p>
            {verified ? (
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-emerald-700 mt-0.5" />
                  <div>
                    <p className="font-bold text-stone-900">{verified.entity_name}</p>
                    <p className="text-[12px] text-stone-500 tabular-nums">ABN {verified.abn}</p>
                    {verified.business_name && verified.business_name !== verified.entity_name && (
                      <p className="text-[12px] text-emerald-700 mt-0.5">
                        Trading as <strong>{verified.business_name}</strong>
                      </p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-[11px]">
                  <Stat label="Status"     value={verified.status} ok={verified.status === "verified"} />
                  <Stat label="Source"     value={verified.cached ? "Registry cache" : "Live ABR"} />
                  <Stat label="Trading names" value={(verified.trading_names || []).length} />
                  <Stat label="Linked modules" value={Object.keys(verified.modules || {}).length} />
                </div>
              </div>
            ) : (
              <p className="text-[12px] text-stone-500 italic">Paste an ABN on the left to see the registry in action.</p>
            )}
          </div>

          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-2">
              Step 2 · cross-module links
            </p>
            <div className="grid grid-cols-2 gap-2">
              {MODULES.map((m) => {
                const links = verified?.modules?.[m.key] || [];
                const active = links.length > 0;
                return (
                  <div
                    key={m.key}
                    data-testid={`module-tile-${m.key}`}
                    className={`rounded-lg p-3 border ${active
                      ? "bg-emerald-50 border-emerald-300"
                      : "bg-white border-stone-200 opacity-70"}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-[13px] font-bold text-stone-900">{m.label}</p>
                      {active && (
                        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-emerald-200 text-emerald-900">
                          {links.length} link{links.length > 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-stone-500 mt-0.5">{m.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-lg bg-stone-900 text-stone-100 p-4 text-[12px] leading-relaxed">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-3.5 h-3.5 text-amber-400" />
              <p className="text-[10px] uppercase tracking-wider font-bold text-amber-400">How it works</p>
            </div>
            <ol className="space-y-1 list-decimal list-inside text-stone-300">
              <li>Strip non-digits — pasting any format works.</li>
              <li>Check the registry. Cached and fresh ({"<"}90 days)? Return instantly.</li>
              <li>Stale? Return cached AND refresh from ABR in the background.</li>
              <li>Missing? Hit ABR live, persist the result, return.</li>
              <li>On submit, modules call POST /api/abn/link to register the cross-reference.</li>
            </ol>
            <p className="mt-3 flex items-center gap-1 text-amber-400 font-semibold">
              Same ABN, every module, one source of truth <ArrowRight className="w-3 h-3" />
            </p>
          </div>

          {/* Directory backfill progress — auto-polls every 15s */}
          {backfill && (
            <div
              className="rounded-xl border border-stone-300 bg-white p-4 space-y-3"
              data-testid="backfill-panel"
            >
              <div className="flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-amber-700" />
                <p className="text-[10px] uppercase tracking-wider font-bold text-amber-700">
                  Directory ABN backfill — live
                </p>
              </div>
              <p className="text-[12px] text-stone-600">
                Walking every directory listing, asking the ABR for a name + postcode match.
                Records that come back with no result are flagged{" "}
                <span className="font-mono text-[11px] px-1 py-0.5 rounded bg-stone-100">no_match</span>
                {" "}so we can prove we searched — not just assumed.
              </p>
              <div className="h-2 w-full bg-stone-200 rounded overflow-hidden">
                <div
                  data-testid="backfill-progress-bar"
                  className="h-full bg-emerald-500 transition-all"
                  style={{ width: `${backfill.pct_searched}%` }}
                />
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <BfStat label="Searched" v={`${backfill.searched.toLocaleString()} / ${backfill.total.toLocaleString()}`} sub={`${backfill.pct_searched}%`} />
                <BfStat label="Matched" v={backfill.matched.toLocaleString()} tone="good" />
                <BfStat label="Ambiguous" v={backfill.ambiguous.toLocaleString()} tone="warn" />
                <BfStat label="No-match (confirmed)" v={backfill.no_match.toLocaleString()} />
                <BfStat label="Errors (will retry)" v={backfill.error.toLocaleString()} />
                <BfStat label="Not yet searched" v={backfill.unsearched.toLocaleString()} />
              </div>

              {/* Address-match breakdown — for matched records only.
                  Surfaces the insight that most AU SMBs register their
                  ABN at the accountant's office, not where they operate. */}
              {backfill.address_match && backfill.matched > 0 && (
                <div className="pt-2 border-t border-stone-200">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1.5">
                    Of matched · where the ABN address sits
                  </p>
                  <div className="space-y-1 text-[11px]">
                    <AddrRow
                      label="Exact postcode match"
                      n={backfill.address_match.exact_postcode || 0}
                      total={backfill.matched}
                      tone="emerald"
                    />
                    <AddrRow
                      label="Different suburb, same state"
                      n={backfill.address_match.same_state || 0}
                      total={backfill.matched}
                      tone="sky"
                    />
                    <AddrRow
                      label="Accountant / head-office address (different state)"
                      n={backfill.address_match.registered_office_differs || 0}
                      total={backfill.matched}
                      tone="amber"
                    />
                  </div>
                </div>
              )}

              {/* Self-healing telemetry — how many records are backfill
                  vs human-claimed. As venues claim their listings the
                  user_claim count climbs and the registry self-heals. */}
              {backfill.sources && (
                <div className="pt-2 border-t border-stone-200">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1.5">
                    Source of truth · self-healing flywheel
                  </p>
                  <div className="space-y-1 text-[11px]">
                    <AddrRow
                      label="Auto-backfilled (bot)"
                      n={backfill.sources.backfill_v1 || 0}
                      total={backfill.searched}
                      tone="sky"
                    />
                    <AddrRow
                      label="Human-claimed (owner verified)"
                      n={backfill.sources.user_claim || 0}
                      total={backfill.searched}
                      tone="emerald"
                    />
                  </div>
                  {backfill.confidence && (
                    <div className="mt-2 pt-2 border-t border-stone-100">
                      <p className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1.5">
                        Registry confidence ladder
                      </p>
                      <div className="flex gap-1.5 flex-wrap text-[10px]">
                        {Object.entries({
                          high:   { label: "High (claimed)",    cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
                          medium: { label: "Medium (exact/same-state)", cls: "bg-sky-100 text-sky-900 border-sky-300" },
                          low:    { label: "Low (cross-state)",  cls: "bg-amber-100 text-amber-900 border-amber-300" },
                        }).map(([k, v]) => (
                          <span
                            key={k}
                            data-testid={`conf-${k}`}
                            className={`px-2 py-0.5 rounded-full border font-semibold ${v.cls}`}
                          >
                            {v.label} · {(backfill.confidence[k] || 0).toLocaleString()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* abn_status enum — distinguishes "we searched, no result"
                  from "ABN not required" (private sale, individual host)
                  from "we never looked".  Investors love that this
                  surface explicitly differentiates the four states. */}
              {backfill.abn_status && (
                <div className="pt-2 border-t border-stone-200">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-stone-500 mb-1.5">
                    ABN status across the directory
                  </p>
                  <div className="flex gap-1.5 flex-wrap text-[10px]">
                    {[
                      { k: "verified",          label: "Verified",            cls: "bg-emerald-100 text-emerald-900 border-emerald-300" },
                      { k: "searched_no_match", label: "Searched · no match", cls: "bg-stone-100  text-stone-700   border-stone-300" },
                      { k: "not_required",      label: "ABN not required",    cls: "bg-violet-100 text-violet-900  border-violet-300" },
                      { k: "pending",           label: "Pending owner input", cls: "bg-amber-100  text-amber-900   border-amber-300" },
                      { k: "unsearched",        label: "Not searched yet",    cls: "bg-rose-100   text-rose-900    border-rose-300" },
                    ].map(({ k, label, cls }) => (
                      <span
                        key={k}
                        data-testid={`status-${k}`}
                        className={`px-2 py-0.5 rounded-full border font-semibold ${cls}`}
                      >
                        {label} · {(backfill.abn_status[k] || 0).toLocaleString()}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function Stat({ label, value, ok }) {
  return (
    <div className="bg-stone-50 rounded px-2 py-1.5">
      <p className="text-[9px] uppercase tracking-wider font-bold text-stone-500">{label}</p>
      <p className={`font-bold ${ok ? "text-emerald-700" : "text-stone-900"}`}>{value}</p>
    </div>
  );
}

function BfStat({ label, v, sub, tone }) {
  const colour =
    tone === "good" ? "text-emerald-700" :
    tone === "warn" ? "text-amber-700" :
    "text-stone-900";
  return (
    <div className="bg-stone-50 rounded px-2 py-1.5">
      <p className="text-[9px] uppercase tracking-wider font-bold text-stone-500">{label}</p>
      <p className={`font-bold tabular-nums ${colour}`}>{v}{sub ? <span className="text-stone-400 font-normal ml-1">({sub})</span> : null}</p>
    </div>
  );
}

function AddrRow({ label, n, total, tone }) {
  const pct = total ? Math.round((n / total) * 100) : 0;
  const bar = tone === "amber" ? "bg-amber-500"
            : tone === "sky"   ? "bg-sky-500"
            : "bg-emerald-500";
  return (
    <div className="flex items-center gap-2">
      <span className="text-stone-600 flex-1 truncate">{label}</span>
      <div className="w-20 h-1.5 bg-stone-200 rounded overflow-hidden">
        <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="tabular-nums font-bold text-stone-900 w-14 text-right">
        {n.toLocaleString()} <span className="text-stone-400 font-normal">{pct}%</span>
      </span>
    </div>
  );
}
